import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  catchError,
  exhaustMap,
  finalize,
  map,
  Observable,
  of,
  share,
  switchMap,
  takeWhile,
  tap,
  timer,
} from 'rxjs';
import { ENVIRONMENT } from '../models';
import {
  Auth,
  CreateDeploymentTask,
  DeploymentTask,
  MultipleDeploymentTasksResponse,
  DeviceInfo,
  SingleDeploymentTaskResponse,
  SoftwarePackage,
  MultipleSoftwarePackagesResponse,
  DeploymentType,
  TaskNotCancelableError,
  canTrackTask,
} from '../models/software-package';
import { LazyLoadRequestParams, Logger } from '../utils';
import { MessageService } from 'primeng/api';

@Injectable({
  providedIn: 'root',
})
export class SoftwareDeploymentService {
  private sdmUrl = inject(ENVIRONMENT).sdmUrl;
  private readonly logger = new Logger(this.constructor.name);
  private trackedDeployments: Record<string, Observable<DeploymentTask>> = {};

  constructor(
    private httpClient: HttpClient,
    private messageService: MessageService,
  ) {}

  getSoftwarePackages(deviceInfo: DeviceInfo): Observable<SoftwarePackage[]> {
    const pid = this.getEncodedPid(deviceInfo);
    return this.httpClient
      .get<MultipleSoftwarePackagesResponse>(`${this.sdmUrl}/packages`, {
        params: {
          pid,
        },
      })
      .pipe(map(packages => packages.data.map(p => new SoftwarePackage(p))));
  }

  getDeployments(
    deviceInfo: DeviceInfo,
    queryFilter?: Partial<LazyLoadRequestParams>,
  ): Observable<DeploymentTask[]> {
    const pid = this.getEncodedPid(deviceInfo);
    const orderBy = queryFilter?.order || 'createdDate';
    const sortDirection = queryFilter?.dir || 'desc';
    const deploymentType: DeploymentType = 'scheduled';
    const params: Record<string, string | number | boolean> = {
      pid,
      type: deploymentType,
      sort: `${orderBy}:${sortDirection}`,
    };

    return this.httpClient
      .get<MultipleDeploymentTasksResponse>(`${this.sdmUrl}/deployment-tasks`, {
        params,
      })
      .pipe(
        map(deploymentTasks =>
          deploymentTasks.data.map(deploymentTask => new DeploymentTask(deploymentTask)),
        ),
      );
  }

  getSingleDeployment(id: string): Observable<DeploymentTask> {
    return this.httpClient
      .get<SingleDeploymentTaskResponse>(`${this.sdmUrl}/deployment-tasks/${id}`)
      .pipe(map(deploymentTask => new DeploymentTask(deploymentTask.data)));
  }

  trackDeploymentUntilComplete(deploymentTask: DeploymentTask): Observable<DeploymentTask> {
    if (!canTrackTask(deploymentTask)) {
      return of(deploymentTask);
    }

    if (!this.trackedDeployments[deploymentTask.id]) {
      // This observable will complete only, if the tracked deployment moves to a final state.
      this.trackedDeployments[deploymentTask.id] = timer(0, 5000).pipe(
        exhaustMap(() =>
          this.getSingleDeployment(deploymentTask.id).pipe(
            catchError(error => {
              this.logger.error(
                `Error fetching status for deployment task = ${deploymentTask.id}. Details = `,
                error,
              );
              throw error;
            }),
          ),
        ),
        takeWhile(updatedTask => canTrackTask(updatedTask), true),
        share(),
        finalize(() => delete this.trackedDeployments[deploymentTask.id]),
      );
    }

    return this.trackedDeployments[deploymentTask.id];
  }

  createDeploymentTask(deviceDeployment: CreateDeploymentTask): Observable<DeploymentTask> {
    const pid = this.getPid(deviceDeployment.deviceInfo);
    const encodedCredentials = this.encodeCredentials(deviceDeployment.auth);
    return this.httpClient
      .post<SingleDeploymentTaskResponse>(`${this.sdmUrl}/deployment-tasks`, {
        pid,
        thingId: deviceDeployment.thingId,
        systemThingId: deviceDeployment.serviceThingId
          ? `urn:system:${deviceDeployment.serviceThingId}`
          : undefined,
        thingDescriptionDirectoryUrl: deviceDeployment.thingDescriptionDirectoryUrl,
        packageId: deviceDeployment.packageId,
        description: deviceDeployment.description,
        type: deviceDeployment.type,
        deployDate: deviceDeployment.deployDate?.toISOString(),
        auth: encodedCredentials,
        conditions: deviceDeployment.conditions,
      })
      .pipe(
        map(deploymentTask => new DeploymentTask(deploymentTask.data)),
        catchError(error => {
          this.logger.error('Failed to create new Task. Error = ', error);
          this.messageService.add({
            severity: 'error',
            summary: 'device-firmware.deployment-failed',
            life: 10000,
          });
          throw error;
        }),
      );
  }

  cancelDeploymentTask(id: string): Observable<DeploymentTask> {
    return this.getSingleDeployment(id).pipe(
      tap(deploymentTask => {
        if (!deploymentTask.cancelable) {
          throw new TaskNotCancelableError();
        }
      }),
      switchMap(() =>
        this.httpClient
          .put<SingleDeploymentTaskResponse>(`${this.sdmUrl}/deployment-tasks/${id}`, {
            status: 'cancelled',
          })
          .pipe(map(response => new DeploymentTask(response.data))),
      ),
      catchError(error => {
        this.logger.error('Failed to cancel Task. Error = ', error);
        if (error instanceof TaskNotCancelableError) {
          this.messageService.add({
            severity: 'error',
            summary: 'device-firmware.cancel-deployment-failed-already-processing',
            life: 10000,
          });
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'device-firmware.cancel-deployment-failed-general',
            life: 10000,
          });
        }
        throw error;
      }),
    );
  }

  private encodeCredentials(auth: Auth) {
    const jsonCredentials = JSON.stringify(auth);
    return btoa(jsonCredentials);
  }

  private getEncodedPid(deviceInfo: DeviceInfo) {
    return encodeURIComponent(this.getPid(deviceInfo));
  }

  private getPid(deviceInfo: DeviceInfo) {
    return `https://pid.sick.com/${deviceInfo.partNumber}/${deviceInfo.serialNumber}`;
  }
}
